/**
 * 병의원·약국 영업 상태 (PartnerBusinessStatus).
 * 필터 파라미터·응답 businessStatusType 이 같은 enum을 쓴다.
 */
export const PARTNER_BUSINESS_STATUS = {
  OPEN: 'OPEN',
  SUSPENDED: 'SUSPENDED',
  CLOSED: 'CLOSED',
} as const;

export type PartnerBusinessStatus =
  (typeof PARTNER_BUSINESS_STATUS)[keyof typeof PARTNER_BUSINESS_STATUS];

export const PARTNER_BUSINESS_STATUS_LABEL: Record<
  PartnerBusinessStatus,
  string
> = {
  OPEN: '정상',
  SUSPENDED: '휴업',
  CLOSED: '폐업',
};

/** Badge variant — 정상=초록, 휴업=주황, 폐업=빨강 */
export type PartnerBusinessStatusBadgeVariant =
  | 'success'
  | 'warning'
  | 'destructive'
  | 'outline';

export const PARTNER_BUSINESS_STATUS_BADGE_VARIANT: Record<
  PartnerBusinessStatus,
  PartnerBusinessStatusBadgeVariant
> = {
  OPEN: 'success',
  SUSPENDED: 'warning',
  CLOSED: 'destructive',
};

/**
 * UI·URL 값을 API 필터 enum으로 정규화한다.
 * 전체(`all`/`전체`/빈 값)는 undefined. 레거시 한글도 허용한다.
 */
export const toPartnerBusinessStatusFilter = (
  value?: string | null,
): PartnerBusinessStatus | undefined => {
  if (!value || value === 'all' || value === '전체') return undefined;
  if (value === PARTNER_BUSINESS_STATUS.OPEN || value === '정상') {
    return PARTNER_BUSINESS_STATUS.OPEN;
  }
  if (value === PARTNER_BUSINESS_STATUS.SUSPENDED || value === '휴업') {
    return PARTNER_BUSINESS_STATUS.SUSPENDED;
  }
  if (value === PARTNER_BUSINESS_STATUS.CLOSED || value === '폐업') {
    return PARTNER_BUSINESS_STATUS.CLOSED;
  }
  return undefined;
};

/** 뱃지·셀 표시용 한글 라벨. 미지 값은 '-' */
export const getPartnerBusinessStatusLabel = (
  value?: string | null,
): string => {
  if (!value) return '-';
  if (value in PARTNER_BUSINESS_STATUS_LABEL) {
    return PARTNER_BUSINESS_STATUS_LABEL[value as PartnerBusinessStatus];
  }
  return '-';
};

/** 영업 상태 Badge 색상. 미지 값은 outline */
export const getPartnerBusinessStatusBadgeVariant = (
  value?: string | null,
): PartnerBusinessStatusBadgeVariant => {
  if (value && value in PARTNER_BUSINESS_STATUS_BADGE_VARIANT) {
    return PARTNER_BUSINESS_STATUS_BADGE_VARIANT[
      value as PartnerBusinessStatus
    ];
  }
  return 'outline';
};

/**
 * 요양기관기호 표시.
 * 폐업 sentinel `"DELETED"` 와 null/빈 값은 '-' 로 보여 준다.
 */
export const formatYkihoDisplay = (ykiho?: string | null): string => {
  const value = ykiho?.trim();
  if (!value || value === 'DELETED') return '-';
  return value;
};
